import React from 'react';
import { X, Loader2 } from 'lucide-react';

interface VerificationModalProps {
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  success: boolean;
  onClose: () => void;
  onVerify: () => void;
}

/**
 * VerificationModal - Palm verification demo modal
 *
 * Layout:
 * - Palm scan illustration
 * - Title and description
 * - Single green action button
 * - Close button (top right)
 */
const VerificationModal: React.FC<VerificationModalProps> = ({
  isOpen,
  isLoading,
  error,
  success,
  onClose,
  onVerify,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-[16px]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-[407px] bg-[#151918] border border-[#212827] rounded-[32px] overflow-hidden pt-6 pb-8 px-8">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-8 p-1 text-white/40 hover:text-white/70 transition-colors cursor-pointer"
          aria-label="Close modal"
        >
          <X className="w-4 h-4" />
        </button>


        {/* Palm Scan Illustration */}
        <div className="flex justify-center mb-6">
          <div className="w-[200px] h-[200px] flex items-center justify-center">
            <PalmScanIllustration />
          </div>
        </div>

        {/* Title & Description */}
        <div className="text-center space-y-2 mb-6">
          <h2 className="text-base font-semibold text-white leading-[1.2]">
            {success
              ? 'Verification Complete!'
              : error
              ? 'Verification Failed'
              : 'Simulate Palm Scan (Demo)'}
          </h2>
          <p className="text-sm text-[#bfbfbf] leading-[1.5]">
            {success
              ? 'Your humanity has been verified. You can now claim your airdrop.'
              : error
              ? error
              : 'In the live app, you would scan your palm using the Humanity App. For this demo, simply click the button to simulate a successful verification.'}
          </p>
        </div>

        {/* Action Button */}
        {success ? (
          <button
            onClick={onClose}
            className="w-full h-10 rounded-lg bg-[#6dfb3f] hover:bg-[#5de935] text-[#103c01] text-sm font-semibold transition-colors cursor-pointer"
          >
            Done
          </button>
        ) : error ? (
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 h-10 rounded-lg bg-[#212827] hover:bg-[#2a3230] text-white text-sm font-semibold transition-colors cursor-pointer"
            >
              Close
            </button>
            <button
              onClick={onVerify}
              disabled={isLoading}
              className="flex-1 h-10 rounded-lg bg-[#6dfb3f] hover:bg-[#5de935] text-[#103c01] text-sm font-semibold transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Retry
            </button>
          </div>
        ) : (
          <button
            onClick={onVerify}
            disabled={isLoading}
            className="w-full h-10 rounded-lg bg-[#6dfb3f] hover:bg-[#5de935] text-[#103c01] text-sm font-semibold transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Verifying...</span>
              </>
            ) : (
              'Mock Successful Scan'
            )}
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * PalmScanIllustration - SVG illustration of palm scanning with phone
 * Based on Figma design node 317:2681
 */
const PalmScanIllustration: React.FC = () => (
  <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Phone left */}
    <rect x="30" y="30" width="60" height="110" rx="8" stroke="white" strokeOpacity="0.3" strokeWidth="1.5" fill="none" />
    <rect x="36" y="40" width="48" height="90" rx="4" stroke="white" strokeOpacity="0.15" strokeWidth="1" fill="none" />

    {/* Hand outline */}
    <g stroke="white" strokeOpacity="0.5" strokeWidth="1.5" fill="none">
      {/* Palm */}
      <path d="M85 130 C85 130, 80 100, 82 80 C83 70, 88 55, 92 50 C96 45, 100 48, 99 55 L97 75" />
      {/* Index finger */}
      <path d="M97 75 C97 65, 97 50, 100 42 C103 35, 108 37, 107 45 L105 70" />
      {/* Middle finger */}
      <path d="M105 70 C105 58, 106 43, 110 36 C113 30, 118 32, 117 40 L114 68" />
      {/* Ring finger */}
      <path d="M114 68 C114 58, 115 47, 118 41 C121 35, 126 38, 124 46 L121 70" />
      {/* Pinky */}
      <path d="M121 70 C121 62, 122 52, 125 48 C128 44, 132 47, 130 54 L127 72" />
      {/* Palm bottom */}
      <path d="M127 72 C130 85, 128 100, 125 115 C122 125, 110 135, 95 135 C85 135, 85 130, 85 130" />
    </g>

    {/* Phone right (with green glow) */}
    <rect x="120" y="50" width="55" height="90" rx="8" stroke="white" strokeOpacity="0.3" strokeWidth="1.5" fill="none" />
    <rect x="126" y="58" width="43" height="74" rx="4" stroke="white" strokeOpacity="0.15" strokeWidth="1" fill="none" />

    {/* Fingerprint scan effect on right phone */}
    <circle cx="147" cy="92" r="14" fill="#6dfb3f" fillOpacity="0.15" />
    <circle cx="147" cy="92" r="10" fill="#6dfb3f" fillOpacity="0.25" />
    <circle cx="147" cy="92" r="6" fill="#6dfb3f" fillOpacity="0.4" />

    {/* Fingerprint lines */}
    <g stroke="#6dfb3f" strokeOpacity="0.6" strokeWidth="0.8" fill="none">
      <path d="M143 88 C143 84, 147 82, 151 84 C155 86, 155 90, 153 93" />
      <path d="M144 90 C144 87, 147 85, 150 87 C153 89, 152 92, 150 94" />
      <path d="M145 92 C145 90, 147 89, 149 90 C151 91, 150 93, 148 94" />
    </g>

    {/* Scan lines effect */}
    <line x1="85" y1="95" x2="120" y2="80" stroke="#6dfb3f" strokeOpacity="0.15" strokeWidth="0.5" strokeDasharray="3 3" />
    <line x1="90" y1="105" x2="125" y2="90" stroke="#6dfb3f" strokeOpacity="0.1" strokeWidth="0.5" strokeDasharray="3 3" />
  </svg>
);

export default VerificationModal;
